import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Card, ErrorNote, Skeleton } from './Home.js';

interface ActivityEvent {
  id: string;
  at: string;
  kind: string;
  summary: string;
}

const KIND_GLYPH: Record<string, string> = {
  statement_parsed: '✓',
  statement_failed: '✕',
  statement_needs_review: '⚠',
  passbook_reconciled: '↺',
  login_ok: '·',
  login_failed: '✕',
  pii_reveal: '👁',
  member_created: '+',
  campaign_assigned: '◎',
  purge_executed: '🗑',
};

/**
 * Insights — Activity segment (docs/SCREEN-SPECS.md §7): the trust surface.
 * Errors appear IN the timeline as history, not as popups. Reports arrive in
 * a later phase with the chart layer.
 */
export function Insights() {
  const { data, isPending, error } = useQuery({
    queryKey: ['activity'],
    queryFn: () => api.get<ActivityEvent[]>('/activity?limit=50'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  // Month-sectioned, reverse-chron — provenance is chronological by nature.
  const byMonth = new Map<string, ActivityEvent[]>();
  for (const e of data) {
    const month = new Date(e.at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    if (!byMonth.has(month)) byMonth.set(month, []);
    byMonth.get(month)!.push(e);
  }

  // The month's pulse, computed from the same events the timeline shows —
  // a summary that could disagree with its own detail would be worse than none.
  const parsed = data.filter((e) => e.kind === 'statement_parsed').length;
  const review = data.filter((e) => e.kind === 'statement_needs_review').length;
  const failed = data.filter((e) => e.kind.includes('failed')).length;

  return (
    <div>
      <h1 className="page-title">Insights</h1>
      <p className="page-sub">
        Everything the system does is logged here — statements, sign-ins, reveals.
      </p>

      <div className="stat-grid">
        <div className="stat">
          <span className="stat-label">Statements parsed</span>
          <span className="stat-value delta-pos">{parsed}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Needing review</span>
          <span className={`stat-value ${review > 0 ? 'state-warn' : ''}`}>{review}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Failures</span>
          <span className={`stat-value ${failed > 0 ? 'delta-neg' : ''}`}>{failed}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Last activity</span>
          <span className="stat-value">
            {data[0] ? new Date(data[0].at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
          </span>
        </div>
      </div>

      {data.length === 0 && (
        <Card title="Activity">
          <div className="empty">
            <span className="empty-glyph" aria-hidden>≡</span>
            Every statement, parse and update will be logged here — automatically.
          </div>
        </Card>
      )}

      {[...byMonth.entries()].map(([month, events]) => (
        <Card key={month} title={month}>
          {events.map((e) => (
            <div
              key={e.id}
              style={{
                display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline',
                padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-separator)',
                font: 'var(--type-subhead)',
              }}
            >
              <span aria-hidden="true" style={{ width: 18, textAlign: 'center', flexShrink: 0, color: kindColor(e.kind) }}>
                {KIND_GLYPH[e.kind] ?? '·'}
              </span>
              <span style={{ flex: 1 }}>{e.summary}</span>
              <time dateTime={e.at} style={{ font: 'var(--type-footnote)', color: 'var(--color-label-tertiary)', flexShrink: 0 }}>
                {new Date(e.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </time>
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

function kindColor(kind: string): string {
  if (kind.includes('failed')) return 'var(--color-status-critical)';
  if (kind.includes('review')) return 'var(--color-status-warning)';
  if (kind === 'statement_parsed' || kind === 'passbook_reconciled') return 'var(--color-status-good)';
  return 'var(--color-label-tertiary)';
}
